export const C = {
  // Core Palette — light corporate theme
  bg:          "#F8FAFC",   // page background
  surface:     "#FFFFFF",   // cards, sidebar, modals, inputs
  surfaceAlt:  "#F1F5F9",   // secondary/hover surface tint
  border:      "#E2E8F0",   // borders & dividers

  navy:        "#1B3A6B",   // primary brand color
  navyDark:    "#152D54",   // hover/active shade of navy
  navyPale:    "#EAF0F8",   // pale navy tint (active nav bg, subtle highlights)

  gold:        "#C9A84C",
  goldLight:   "#FEF3C7",
  goldDark:    "#A8832A",

  emerald:     "#10B981",
  emeraldLight:"#D1FAE5",
  rose:        "#F43F5E",
  roseLight:   "#FFE4E6",
  purple:      "#8B5CF6",
  purpleLight: "#EDE9FE",
  slate:       "#64748B",
  slateLight:  "#F1F5F9",

  text:        "#1E293B",   // primary text
  textMid:     "#334155",   // secondary text
  textMuted:   "#64748B",   // muted / meta text
  textFaint:   "#94A3B8",   // faintest (placeholders, disabled)

  white:       "#FFFFFF",   // text on solid colored buttons/banners — unchanged in any theme

  // Kept for the handful of call sites that still reference these names —
  // all now resolve to sane light-theme values.
  blueLight:   "#1B3A6B",
  blueMid:     "#1B3A6B",
  glassBorder: "#E2E8F0",
};

export const G = {
  // Vivid accent gradients — kept for small icon badges/avatars and the
  // occasional colored highlight banner, NOT for full-page backgrounds.
  blue:    `linear-gradient(135deg, #1B3A6B 0%, #2D5494 100%)`,
  gold:    `linear-gradient(135deg, #A8832A 0%, #C9A84C 100%)`,
  emerald: `linear-gradient(135deg, #059669 0%, #10B981 100%)`,
  rose:    `linear-gradient(135deg, #E11D48 0%, #F43F5E 100%)`,
  purple:  `linear-gradient(135deg, #7C3AED 0%, #8B5CF6 100%)`,
  // Full-page/sidebar surfaces are flat and light now — no dark gradients.
  hero:    `linear-gradient(135deg, #F8FAFC 0%, #EEF2F7 55%, #E9EFF7 100%)`,
  sidebar: `#FFFFFF`,
  card:    `#FFFFFF`,
};

// Animation keyframes injected once
export const ANIMATIONS = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');
* { box-sizing: border-box; margin: 0; padding: 0; }
body { font-family: 'Inter', sans-serif; background: #F8FAFC; color: #1E293B; }
::-webkit-scrollbar { width: 5px; height: 5px; }
::-webkit-scrollbar-track { background: #F1F5F9; }
::-webkit-scrollbar-thumb { background: #CBD5E1; border-radius: 3px; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
@keyframes slideIn { from { opacity: 0; transform: translateX(-16px); } to { opacity: 1; transform: translateX(0); } }
@keyframes pulse { 0%,100% { opacity: 0.4; transform: scale(0.85); } 50% { opacity: 1; transform: scale(1.15); } }
@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
@keyframes shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
@keyframes bounceIn { 0% { transform: scale(0.3); opacity: 0; } 50% { transform: scale(1.05); } 70% { transform: scale(0.9); } 100% { transform: scale(1); opacity: 1; } }
@keyframes glow { 0%,100% { box-shadow: 0 0 0 rgba(27,58,107,0); } 50% { box-shadow: 0 0 12px rgba(27,58,107,0.15); } }
@keyframes float { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-6px); } }
.fade-in { animation: fadeIn 0.4s ease forwards; }
.slide-in { animation: slideIn 0.3s ease forwards; }
.bounce-in { animation: bounceIn 0.5s cubic-bezier(0.36,0.07,0.19,0.97) forwards; }
`;

export const S = {
  // Layout
  appWrap: { minHeight: "100vh", background: C.bg, fontFamily: "'Inter',sans-serif", color: C.text },
  layout: { display: "flex", minHeight: "100vh" },
  sidebar: {
    width: 270, background: C.surface, display: "flex", flexDirection: "column",
    position: "fixed", top: 0, left: 0, height: "100vh", zIndex: 100,
    borderRight: `1px solid ${C.border}`, overflowY: "auto",
  },
  mainArea: { marginLeft: 270, flex: 1, display: "flex", flexDirection: "column", minHeight: "100vh", background: C.bg },
  topbar: {
    background: C.surface,
    borderBottom: `1px solid ${C.border}`, padding: "14px 32px",
    display: "flex", alignItems: "center", justifyContent: "space-between",
    position: "sticky", top: 0, zIndex: 50,
  },
  content: { flex: 1, padding: "28px 32px", overflowY: "auto" },

  // Cards — flat white with a subtle shadow, no glassmorphism
  glassCard: {
    background: C.surface,
    border: `1px solid ${C.border}`, borderRadius: 16,
    boxShadow: "0 1px 3px rgba(15,23,42,0.06)",
    transition: "all 0.2s ease",
  },
  card: {
    background: C.surface, border: `1px solid ${C.border}`,
    borderRadius: 16, overflow: "hidden",
    boxShadow: "0 1px 3px rgba(15,23,42,0.06)",
  },
  cardHover: {
    background: C.surface, border: `1px solid ${C.border}`,
    borderRadius: 16, overflow: "hidden", cursor: "pointer",
    boxShadow: "0 1px 3px rgba(15,23,42,0.06)",
    transition: "all 0.2s ease", transform: "translateY(0)",
  },

  // Inputs
  formGroup: { marginBottom: 20 },
  label: { display: "block", fontSize: 12, fontWeight: 600, color: C.textMuted, marginBottom: 7, textTransform: "uppercase", letterSpacing: 0.5 },
  input: {
    width: "100%", padding: "12px 16px", borderRadius: 10,
    border: `1.5px solid ${C.border}`,
    background: C.surface, color: C.text,
    fontSize: 14, outline: "none", transition: "border 0.2s",
    fontFamily: "inherit", boxSizing: "border-box",
    colorScheme: "light",
  },

  // Buttons — solid fills, no gradients, subtle shadow instead of glow
  btn: (v = "primary", sz = "md") => {
    const variants = {
      primary: { bg: C.navy,     color: C.white, border: "none", shadow: "0 1px 2px rgba(27,58,107,0.25)" },
      gold:    { bg: C.gold,     color: C.text,  border: "none", shadow: "0 1px 2px rgba(201,168,76,0.3)" },
      success: { bg: C.emerald,  color: C.white, border: "none", shadow: "0 1px 2px rgba(16,185,129,0.25)" },
      danger:  { bg: C.rose,     color: C.white, border: "none", shadow: "0 1px 2px rgba(244,63,94,0.25)" },
      purple:  { bg: C.purple,   color: C.white, border: "none", shadow: "0 1px 2px rgba(139,92,246,0.25)" },
      ghost:   { bg: "transparent", color: C.textMuted, border: "none", shadow: "none" },
      outline: { bg: "transparent", color: C.navy, border: `1.5px solid ${C.navy}`, shadow: "none" },
      glass:   { bg: C.surfaceAlt, color: C.text, border: `1px solid ${C.border}`, shadow: "none" },
    };
    const sizes = {
      sm: { padding: "6px 14px", fontSize: 12, borderRadius: 8 },
      md: { padding: "10px 20px", fontSize: 14, borderRadius: 10 },
      lg: { padding: "13px 28px", fontSize: 15, borderRadius: 12 },
    };
    const cfg = variants[v] || variants.primary;
    const s = sizes[sz] || sizes.md;
    return {
      background: cfg.bg, color: cfg.color, border: cfg.border || "none",
      boxShadow: cfg.shadow, borderRadius: s.borderRadius, padding: s.padding,
      fontSize: s.fontSize, fontWeight: 600, cursor: "pointer",
      display: "inline-flex", alignItems: "center", gap: 7,
      transition: "all 0.2s ease", whiteSpace: "nowrap", fontFamily: "inherit",
    };
  },

  // Badges — pale pastel background, saturated text (no transparency tricks)
  badge: (type) => {
    const map = {
      paid:      [C.emeraldLight, "#059669"],
      partial:   [C.goldLight,    C.goldDark],
      unpaid:    [C.roseLight,    "#E11D48"],
      success:   [C.emeraldLight, "#059669"],
      warning:   [C.goldLight,    C.goldDark],
      danger:    [C.roseLight,    "#E11D48"],
      info:      ["#DBEAFE",      "#1D4ED8"],
      purple:    [C.purpleLight,  "#7C3AED"],
      gold:      [C.goldLight,    C.goldDark],
      gray:      [C.surfaceAlt,   C.textMuted],
      locked:    [C.surfaceAlt,   C.textMuted],
      general:   ["#DBEAFE",      "#1D4ED8"],
      core:      [C.purpleLight,  "#7C3AED"],
      specialist:[C.goldLight,    C.goldDark],
    };
    const [bg, col] = map[type] || map.gray;
    return { display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 10px", borderRadius: 20, fontSize: 12, fontWeight: 600, background: bg, color: col, border: "none" };
  },

  // Modal
  overlay: { position: "fixed", inset: 0, background: "rgba(15,23,42,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 },
  modal: (w = 560) => ({
    background: C.surface,
    border: `1px solid ${C.border}`, borderRadius: 20,
    width: "100%", maxWidth: w, maxHeight: "90vh", overflow: "auto",
    boxShadow: "0 20px 50px rgba(15,23,42,0.18)",
  }),
  modalHead: { padding: "22px 28px", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" },
  modalBody: { padding: 28 },
  modalFoot: { padding: "16px 28px", borderTop: `1px solid ${C.border}`, display: "flex", gap: 12, justifyContent: "flex-end" },

  // Table
  table: { width: "100%", borderCollapse: "collapse" },
  th: { padding: "11px 16px", textAlign: "left", fontSize: 11, fontWeight: 700, color: C.textMuted, background: C.surfaceAlt, borderBottom: `1px solid ${C.border}`, textTransform: "uppercase", letterSpacing: 0.8 },
  td: { padding: "13px 16px", fontSize: 14, color: C.textMid, borderBottom: `1px solid ${C.border}` },

  // Progress
  progressWrap: { height: 8, borderRadius: 4, background: C.surfaceAlt, overflow: "hidden" },
  progressFill: (pct, color = C.navy) => ({ height: "100%", width: `${Math.min(pct, 100)}%`, background: color, borderRadius: 4, transition: "width 0.6s ease" }),

  // Auth
  authWrap: { minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: G.hero, padding: 20, fontFamily: "'Inter',sans-serif" },
  authCard: { background: C.surface, border: `1px solid ${C.border}`, borderRadius: 24, padding: "48px 44px", width: "100%", maxWidth: 460, boxShadow: "0 20px 50px rgba(15,23,42,0.1)" },

  // Misc
  pageTitle: { fontSize: 24, fontWeight: 800, color: C.text, margin: 0 },
  pageSub: { fontSize: 14, color: C.textMuted, marginTop: 4 },
  statsGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px,1fr))", gap: 18, marginBottom: 28 },
  emptyWrap: { textAlign: "center", padding: "56px 24px", color: C.textMuted },
  divider: { height: 1, background: C.border, margin: "20px 0" },
  tabRow: { display: "flex", gap: 4, borderBottom: `1px solid ${C.border}`, marginBottom: 24 },
  tab: (a) => ({ padding: "10px 22px", fontSize: 14, fontWeight: 600, border: "none", background: "none", cursor: "pointer", color: a ? C.navy : C.textMuted, borderBottom: a ? `2px solid ${C.navy}` : "2px solid transparent", marginBottom: -1, transition: "all 0.2s", fontFamily: "inherit" }),
  alert: (t) => {
    const m = { info: ["#EFF6FF", "#1D4ED8"], success: ["#ECFDF5", "#059669"], warning: ["#FFFBEB", C.goldDark], danger: ["#FEF2F2", "#E11D48"] };
    const [bg, col] = m[t] || m.info;
    return { padding: "12px 16px", borderRadius: 10, fontSize: 13, background: bg, color: col, border: `1px solid ${col}22`, marginBottom: 16 };
  },
};

export const uid = () => Math.random().toString(36).slice(2, 9);
export const today = () => new Date().toISOString().split("T")[0];
export const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-PK", { day: "numeric", month: "short", year: "numeric" }) : "—";
export const isPast = (d) => d && new Date(d) < new Date();
export const calcGrade = (p) => !p ? "—" : p >= 90 ? "A+" : p >= 80 ? "A" : p >= 70 ? "B" : p >= 60 ? "C" : "F";
export const genStudentId = (year) => `UNIV-${year || new Date().getFullYear()}-${String(Math.floor(Math.random() * 999) + 1).padStart(3, "0")}`;
